import React, { useContext, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { FiCopy } from "react-icons/fi";
import { createEndpoint } from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";
import {
  EVENTS_GROUPED_WIRED,
  EVENTS_ADVANCED,
} from "../eventCatalogue";

const EventCheckbox = ({ option, checked, onChange }) => (
  <div className="col-md-6 mb-1" key={option.value}>
    <Form.Check
      type="checkbox"
      id={`evt-create-${option.value}`}
      checked={!!checked}
      onChange={(e) => onChange(option.value, e.target.checked)}
      label={
        <span title={option.description}>
          <code className="me-1">{option.label}</code>
          {option.notWired && (
            <span className="text-muted small">(not yet wired)</span>
          )}
        </span>
      }
    />
  </div>
);

const EventPicker = ({ value, onChange, error }) => {
  const selected = new Set(value || []);
  const toggle = (v, on) => {
    const next = new Set(selected);
    if (on) next.add(v);
    else next.delete(v);
    onChange(Array.from(next));
  };
  return (
    <div>
      {EVENTS_GROUPED_WIRED.map((group) => (
        <div key={group.label} className="mb-3">
          <div className="text-muted small text-uppercase mb-1">
            {group.label}
          </div>
          <div className="row">
            {group.options.map((o) => (
              <EventCheckbox
                key={o.value}
                option={o}
                checked={selected.has(o.value)}
                onChange={toggle}
              />
            ))}
          </div>
        </div>
      ))}
      <details className="mb-2">
        <summary className="text-muted small" style={{ cursor: "pointer" }}>
          Advanced — events not yet wired (no deliveries fire yet)
        </summary>
        <div className="row mt-2">
          {EVENTS_ADVANCED.map((o) => (
            <EventCheckbox
              key={o.value}
              option={o}
              checked={selected.has(o.value)}
              onChange={toggle}
            />
          ))}
        </div>
      </details>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
};

const CreateEndpointModal = ({ show, onClose, appPubID, onCreated }) => {
  const { showSuccess, showError, showCopy } = useContext(ToastrContext);
  const [stage, setStage] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      url: "",
      events: [],
      environment: "live",
      description: "",
    },
  });

  const reallyClose = () => {
    setStage("form");
    setCreated(null);
    reset({ url: "", events: [], environment: "live", description: "" });
    onClose();
  };

  const onSubmit = async (data) => {
    if (!data.events || data.events.length === 0) {
      showError("Pick at least one event");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        url: data.url,
        subscribed_events: data.events,
        environment: data.environment,
      };
      if (data.description) body.description = data.description;
      const res = await createEndpoint(appPubID, body);
      setCreated(res);
      setStage("secret");
      onCreated();
      showSuccess("Endpoint created");
    } catch (e) {
      showError((e && e.message) || "Failed to create endpoint");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showCopy(`${label} copied`);
    } catch {
      showError("Copy failed");
    }
  };

  const signingSecret = created ? created.signing_secret || "" : "";

  return (
    <Modal
      show={show}
      onHide={reallyClose}
      centered
      size="lg"
      backdrop={stage === "secret" ? "static" : true}
    >
      {stage === "form" ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Add Webhook Endpoint</Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body>
              <div className="mb-3">
                <label className="col-form-label">
                  URL<span className="required">*</span>
                </label>
                <input
                  type="url"
                  placeholder="https://your-app.example.com/webhooks/otokata"
                  className={`form-control ${
                    errors.url ? "is-invalid" : ""
                  }`}
                  {...register("url", {
                    required: "URL is required",
                    pattern: {
                      value: /^https?:\/\//i,
                      message: "Must start with http:// or https://",
                    },
                  })}
                />
                {errors.url && (
                  <div className="invalid-feedback">{errors.url.message}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="col-form-label">Environment</label>
                <Form.Select {...register("environment", { required: true })}>
                  <option value="live">Live</option>
                  <option value="test">Test</option>
                </Form.Select>
              </div>

              <div className="mb-3">
                <label className="col-form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={2}
                  placeholder="Optional — where this endpoint lives, who owns it"
                  {...register("description")}
                />
              </div>

              <div className="mb-1">
                <label className="col-form-label">
                  Subscribed Events
                  <span className="required">*</span>
                </label>
                <p className="text-muted small mb-2">
                  Pick the events you want delivered to this URL.
                </p>
              </div>
              <Controller
                name="events"
                control={control}
                rules={{
                  validate: (v) =>
                    (v && v.length > 0) || "Pick at least one event",
                }}
                render={({ field }) => (
                  <EventPicker
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.events && errors.events.message}
                  />
                )}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={reallyClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner
                      animation="border"
                      size="sm"
                      className="me-1"
                    />
                    Creating...
                  </>
                ) : (
                  "Create"
                )}
              </Button>
            </Modal.Footer>
          </form>
        </>
      ) : (
        <>
          <Modal.Header>
            <Modal.Title>Save your signing secret</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-warning">
              <strong>This is the only time you'll see this secret.</strong>{" "}
              Use it to verify HMAC signatures on your receiver. You'll need to
              store it on your receiver — it can't be retrieved again.
            </div>
            <div className="mb-3">
              <label className="col-form-label">Signing Secret</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control font-monospace"
                  value={signingSecret}
                  readOnly
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => copy(signingSecret, "Signing secret")}
                  disabled={!signingSecret}
                >
                  <FiCopy />
                </Button>
              </div>
              <div className="form-text">
                Signature header: <code>Otokata-Signature: t=&lt;ts&gt;,v1=&lt;hex_hmac_sha256(secret, ts+"."+body)&gt;</code>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={reallyClose}>
              I've saved it
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default CreateEndpointModal;
